import type { FeatureLike } from 'ol/Feature';
import { LineString } from 'ol/geom';
import { transform } from 'ol/proj';
import { Fill, Stroke, Style } from 'ol/style';

import type { TacticalAnnotationDTO } from '@nora-monorepo/nora-api-client';
import {
  NoraSupportedCoordinateFormats,
} from '@nora-monorepo/shared';

const AREA_CORNERS = 4;
const RELATIVE_COLORS = ['white', 'red', 'black', 'green'] as const;

/**
 * Color for index: 0 = white, 1 = red, 2 = black, 3 = green
 */
function getColorForIndex(sideIndex: number): string {
  const color = RELATIVE_COLORS[sideIndex % AREA_CORNERS];
  return color ?? RELATIVE_COLORS[0];
}

export function getSideStyle(color: string, geometry?: LineString): Style[] {
  return [
    new Style({
      geometry,
      stroke: new Stroke({
        color: 'black',
        width: 6,
      }),
      zIndex: 1,
    }),
    new Style({
      geometry,
      stroke: new Stroke({
        color,
        width: 4,
      }),
      zIndex: 2,
    }),
  ];
}

export function tacticalAnnotationStyleFunction(feature: FeatureLike): Style[] {
  const tacticalAnnotation = feature.get('tacticalAnnotation') as
    | TacticalAnnotationDTO
    | undefined;

  if (tacticalAnnotation && Array.isArray(tacticalAnnotation.lines)) {
    const styles: Style[] = [];

    for (const line of tacticalAnnotation.lines) {
      const startCoordinate = transform(
        [line.start.longitude, line.start.latitude],
        NoraSupportedCoordinateFormats.WGS84,
        NoraSupportedCoordinateFormats.WEB_MERCATOR,
      );
      const endCoordinate = transform(
        [line.end.longitude, line.end.latitude],
        NoraSupportedCoordinateFormats.WGS84,
        NoraSupportedCoordinateFormats.WEB_MERCATOR,
      );

      const color = getColorForIndex(line.index);
      const sideStyle = getSideStyle(
        color,
        new LineString([startCoordinate, endCoordinate]),
      );
      for (const style of sideStyle) {
        styles.push(style);
      }
    }

    // invisible style so the feature is properly detected by OpenLayers but not visually rendered
    styles.push(
      new Style({
        fill: new Fill({ color: 'transparent' }),
        stroke: new Stroke({ color: 'transparent', width: 0 }),
      }),
    );

    return styles;
  }

  const sideIndex = feature.get('sideIndex') as number | undefined ?? 0;
  const color = getColorForIndex(sideIndex);
  return getSideStyle(color);
}
